import { useEffect, useState } from 'react';
import type React from 'react';

import { useI18n } from '@change-corgi/core-react/i18n';

import { usePsfContactDecisionMakerExperiment } from '#src/app/pages/actions/shared/hooks';
import { useCampaignTracking } from '#src/app/shared/hooks/campaignTracking';
import { focus } from '#src/app/shared/utils/dom';

import { EntryModal } from './components/EntryModal';
import { useContactDecisionMakerPaintedDoor } from './hooks/useContactDecisionMakerPaintedDoor';

/**
 * Tier 1 (measurement-only) painted door: after a Supporter Voices comment, shows a "your comment
 * is live" modal offering to send it to the petition's decision maker. Only rendered for eligible,
 * treatment-arm viewers — see `usePsfContactDecisionMakerExperiment`.
 *
 * This currently ships only the entry modal. The chained "contact the decision maker" destination
 * modal — and wiring the entry CTA to open it instead of dismissing — lands in a follow-up PR
 * stacked on top of this one (CHANGE-96886).
 */
export const ContactDecisionMakerPaintedDoor: React.FC<Props> = ({ restoreFocusRef }) => {
	useContactDecisionMakerPaintedDoor();

	const { decisionMaker } = usePsfContactDecisionMakerExperiment();
	const { track } = useCampaignTracking();
	const { translate } = useI18n();
	const [isOpen, setIsOpen] = useState(true);

	// When the modal is dismissed, move focus to the revealed thank-you heading. The modal opens
	// programmatically, so react-aria has no trigger to restore focus to and it falls to `<body>`
	// (WCAG 2.4.3, CHANGE-96802). Deferred a frame so it lands after react-aria tears the overlay down.
	useEffect(() => {
		if (isOpen) return undefined;

		const raf = requestAnimationFrame(() => {
			focus(restoreFocusRef.current?.querySelector('h1'), { makeFocusable: true });
		});

		return () => cancelAnimationFrame(raf);
	}, [isOpen, restoreFocusRef]);

	if (!decisionMaker) return null;

	const close = (): void => setIsOpen(false);

	return (
		<EntryModal
			isOpen={isOpen}
			closeButtonAriaLabel={translate('corgi.components.modal.close_button')}
			decisionMakerName={decisionMaker.displayName}
			decisionMakerTitle={decisionMaker.title || undefined}
			onDismiss={close}
			onSendToDecisionMaker={() => {
				track('campaign_action_psf_contact_decision_maker_entry_click');
				close();
			}}
		/>
	);
};

type Props = {
	/**
	 * The revealed thank-you region to hand focus back to when the modal is dismissed. The modal opens
	 * programmatically (no trigger element), so react-aria has nothing to restore focus to and it falls
	 * to `<body>` — see WCAG 2.4.3 (CHANGE-96802).
	 */
	restoreFocusRef: React.RefObject<HTMLElement | null>;
};
